import { getEnv } from '../../../../helpers/getEnv';
import { isNotNullOrUndefinedValue } from '../../../../helpers/isNotNullOrUndefinedValue';
import type { SuggestionType } from './getPointOfInterestSuggestionType';
import { getPointOfInterestSuggestionType } from './getPointOfInterestSuggestionType';
import { mapSuggestionToSelectedText } from './mapSuggestionToSelectedText';

interface AutoCompletSuggestionResponse {
  suggestions: AutoCompleteSuggestion[];
}

export interface AutoCompleteSuggestion {
  placePrediction: {
    placeId: string;
    structuredFormat: {
      mainText: {
        text: string;
      };
      secondaryText: {
        text: string;
      };
    };
    text: {
      text: string;
    };
    types: string[];
  };
}

const isAutoCompleteSuggestionResponse = (
  value: unknown,
): value is AutoCompletSuggestionResponse => {
  return (
    isNotNullOrUndefinedValue((value as AutoCompletSuggestionResponse).suggestions) &&
    Array.isArray((value as AutoCompletSuggestionResponse).suggestions)
  );
};

export type POISuggestion = {
  value: string;
  suggestionText: string;
  suggestionType: SuggestionType;
  selectedText: string;
};

export const fetchPOISuggestions = (
  query: string,
  sessionToken: string,
): Promise<POISuggestion[] | null> => {
  const searchParams = new URLSearchParams({
    input: query,
    includedRegionCodes: 'au',
    sessionToken,
  });

  const { GOOGLE_PLACES_API_URL, GOOGLE_PLACES_API_KEY } = getEnv;

  const googlePlacesApiUrl = GOOGLE_PLACES_API_URL();
  const googlePlacesApiKey = GOOGLE_PLACES_API_KEY();

  const url = `${googlePlacesApiUrl}:autocomplete?${searchParams.toString()}`;

  return fetch(url, { method: 'POST', headers: { 'X-Goog-Api-Key': googlePlacesApiKey } })
    .then((response) => response.json())
    .then((data) => {
      if (!isAutoCompleteSuggestionResponse(data))
        throw new Error('Failed to parse response from Google Places AutoComplete');

      if (!data.suggestions) return null;
      return data.suggestions.map((suggestion) => ({
        value: suggestion.placePrediction.placeId,
        suggestionText: suggestion.placePrediction.text.text,
        suggestionType: getPointOfInterestSuggestionType(suggestion.placePrediction.types),
        selectedText: mapSuggestionToSelectedText(suggestion),
      }));
    })
    .catch(() => {
      return null;
    });
};
